import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { Link } from 'react-router-dom'

import CloseButton from '../CloseButton/CloseButton'

import './Notification.css'

export const Notification = ({
  children,
  padTop,
  isCloseable = false,
  onClose = () => {},

  isActionable = false,
  actionContent = null,
  actionLink = null,
  onAction = null,

  severity = ''
}) => {
  const [isVisible, toggleVisibility] = useState(true)

  const handleClose = useCallback(() => {
    toggleVisibility(false)
    onClose()
  }, [onClose])
  const handleAction = useCallback(() => {
    if (onAction) {
      onAction()
    }
  }, [onAction])

  if (isVisible) {
    return (
      <div data-testid='notification' styleName={cx('notification', severity, { padTop })}>
        <span styleName='notificationBody'>
          <div data-testid='notification-text' styleName='notificationText'>{children}</div>
          {isCloseable && <CloseButton onClick={handleClose} />}
        </span>
        {isActionable && (
          <span styleName='notificationAction'>
            {
              (actionLink && <Link data-testid='notification-action' styleName='action' to={actionLink}>{actionContent}</Link>) ||
              (onAction && <button data-testid='notification-action' styleName='action' onClick={handleAction}>{actionContent}</button>)
            }
          </span>
        )}
      </div>
    )
  }
  return null
}

export const SeverityShape = PropTypes.oneOf(['error', 'warning'])

Notification.propTypes = {
  children: PropTypes.node.isRequired,

  /**
   * If true, will render a close button that will dismiss this notification.
   */
  isCloseable: PropTypes.bool,
  /**
   * Will be called after this notification is dismissed, if `isCloseable`
   * is true.
   */
  onClose: PropTypes.func,

  /**
   * If true, will render a React Router `Link` or a button (depending on the other props)
   * to serve as a call to action for the user.
   */
  isActionable: PropTypes.bool,
  /**
   * The content to render within the action Link or button, if `isActionable` is true.
   * Can be just text.
   */
  actionContent: PropTypes.node,
  /**
   * Will cause a React Router `Link` to be rendered with this as the `to` prop,
   * if `isActionable` is true.
   */
  actionLink: PropTypes.string,
  /**
   * Will cause a button to be rendered with this as the `onClick` prop,
   * if `isActionable` is true.
   *
   * `actionLink` takes priority if both are specified.
   */
  onAction: PropTypes.func,

  severity: SeverityShape,
  /**
   * If true, will add equal padding on top and bottom of notification
   */
  padTop: PropTypes.bool
}
